// Config for the @hessianhq/ui EnvBanner + EnvSwitcher in the private app
// shell. Resolves the active deployment environment and its display
// metadata from .env, with per-env defaults for the switcher's other
// options.
//
// Env vars consumed (all prefixed PUBLIC_ so they survive the SvelteKit
// public-env partition and reach the browser bundle):
//
//   PUBLIC_ENVIRONMENT   — required. one of:
//                            production | prod | staging | preview |
//                            development | dev | nonprod | review | local
//                          (falls back to Vite's MODE, then 'local')
//                          'nonprod' is the canonical name of our shared
//                          pre-prod deployment (dev-hessian.sh) and maps
//                          onto the 'dev' EnvBanner kind for tone/palette.
//                          Set PUBLIC_ENV_LABEL=nonprod to surface the
//                          deployment's own name in the banner.
//   PUBLIC_ENV_LABEL     — optional. overrides the displayed env label.
//   PUBLIC_ENV_REGION    — optional. region/cluster shown in meta strip.
//   PUBLIC_BUILD_SHA     — optional. short commit SHA appended to meta.
//   PUBLIC_ENV_META      — optional. full override for the meta string;
//                          when set, REGION/SHA are ignored.
//
// The `getEnvOptions` helper builds the switcher list; per-env fallback
// metadata lives in `ENV_DEFAULTS` below so a deployment only has to
// configure its OWN env — sibling envs keep sensible descriptive labels
// for the (currently UI-only) switcher dropdown.

import { env as publicEnv } from '$env/dynamic/public';
import type { EnvBannerEnv, EnvOption } from '@hessianhq/ui';

// Per-environment fallback defaults. The deployment overrides its own
// row via PUBLIC_ENV_REGION / PUBLIC_BUILD_SHA — sibling rows stay
// indicative.
const ENV_DEFAULTS: Record<EnvBannerEnv, { label: string; region: string; role: string }> = {
	prod: { label: 'production', region: 'us-east-1', role: 'primary' },
	staging: { label: 'staging', region: 'us-east-1', role: 'mirror' },
	preview: { label: 'preview', region: 'eu-west-1', role: 'ephemeral' },
	dev: { label: 'dev', region: 'us-east-1', role: 'shared' },
	review: { label: 'review', region: 'us-east-1', role: 'ephemeral' },
	local: { label: 'local', region: '127.0.0.1', role: 'you' }
};

// Order matters — it's the order the switcher menu surfaces options.
const ENV_ORDER: EnvBannerEnv[] = ['prod', 'staging', 'preview', 'dev', 'review', 'local'];

// resolveEnv maps PUBLIC_ENVIRONMENT (or Vite's MODE fallback) onto the
// typed EnvBannerEnv union. Unknown values become 'local' so the banner
// always stays visible — failing closed for a safety indicator.
export function resolveEnv(): EnvBannerEnv {
	const raw = (publicEnv.PUBLIC_ENVIRONMENT ?? import.meta.env.MODE ?? '').toLowerCase();
	if (raw === 'production' || raw === 'prod') return 'prod';
	if (raw === 'staging') return 'staging';
	if (raw === 'preview') return 'preview';
	if (raw === 'development' || raw === 'dev' || raw === 'nonprod') return 'dev';
	if (raw === 'review') return 'review';
	return 'local';
}

// resolveEnvLabel returns the visible env tag (e.g. "production",
// "staging"). PUBLIC_ENV_LABEL wins when set — useful for variants like
// "production-canary" or "staging-eu".
export function resolveEnvLabel(env: EnvBannerEnv): string {
	if (publicEnv.PUBLIC_ENV_LABEL) return publicEnv.PUBLIC_ENV_LABEL;
	return ENV_DEFAULTS[env].label;
}

// resolveEnvMeta builds the right-side meta strip from env vars plus
// per-env defaults. PUBLIC_ENV_META is a full override that bypasses
// the region/role/SHA composition entirely.
export function resolveEnvMeta(env: EnvBannerEnv): string {
	if (publicEnv.PUBLIC_ENV_META) return publicEnv.PUBLIC_ENV_META;

	const defaults = ENV_DEFAULTS[env];
	const region = publicEnv.PUBLIC_ENV_REGION ?? defaults.region;
	const sha = publicEnv.PUBLIC_BUILD_SHA;

	if (sha) return `${region} · ${defaults.role} · build.${sha}`;
	return `${region} · ${defaults.role}`;
}

// getEnvOptions returns the switcher's option list. The CURRENT env's
// meta reflects the live config (so the trigger displays accurate
// region/SHA); other envs use static defaults — the switcher is UI-only
// today, so those rows are indicative.
export function getEnvOptions(currentEnv: EnvBannerEnv): EnvOption[] {
	return ENV_ORDER.map((id) => {
		const defaults = ENV_DEFAULTS[id];
		const meta =
			id === currentEnv
				? resolveEnvMeta(id)
				: `${defaults.region} · ${defaults.role}`;
		return {
			id,
			label: defaults.label,
			kind: id,
			meta
		};
	});
}
